import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { CERTIFIED_INDICATOR_TYPE } from '~src/catalog/components/dataQuality/DataQualityType';
import { updateOrDeleteCertificationForItem } from '~src/catalog/restApi/dataQuality/setDataQualityCertificationForItem';
import { Messages } from '~src/codegen/Localize';
import { IDetailFetcher } from '~src/httpClient/fetchers/IDetailFetcher';
import { IListFetcher } from '~src/httpClient/fetchers/IListFetcher';
import { WebClientDetailFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { FieldValuesQuery } from '~src/httpClient/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IListResource } from '~src/httpClient/resources/Resource';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { GetFieldValuesRequest } from '~src/httpClient/serverApi/requests/GetFieldValuesRequest';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IFieldValue } from '~src/httpClient/serverApi/types/IFieldValue';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { RunExtractRefreshesOnPublishedConnectionRequest } from '~src/publishedConnection/serverApi/requests/RunExtractRefreshesOnPublishedConnectionRequest';
import {
  IPublishedConnectionTableBase,
  IPublishedConnectionTableDetailInfo,
  IPublishedConnectionTableInfo,
} from '~src/publishedConnectionTable/models/IPublishedConnectionTableInfo';
import { PublishedConnectionTableMapper } from '~src/publishedConnectionTable/models/mappers/PublishedConnectionTableMapper';
import { AddTagsToPublishedConnectionTablesRequest } from '~src/publishedConnectionTable/serverApi/requests/AddTagsToPublishedConnectionTablesRequest';
import { GetPublishedConnectionTableActionsRequest } from '~src/publishedConnectionTable/serverApi/requests/GetPublishedConnectionTableActionsRequest';
import { GetPublishedConnectionTablesRequest } from '~src/publishedConnectionTable/serverApi/requests/GetPublishedConnectionTablesRequest';
import { RemoveTagsFromPublishedConnectionTablesRequest } from '~src/publishedConnectionTable/serverApi/requests/RemoveTagsFromPublishedConnectionTablesRequest';
import { IPublishedConnectionTableCertificationParams } from '~src/publishedConnectionTable/serverApi/requests/SetPublishedConnectionTableCertificationRequest';
import {
  SetPublishedConnectionTableDescriptionParams,
  SetPublishedConnectionTableDescriptionRequest
} from '~src/publishedConnectionTable/serverApi/requests/SetPublishedConnectionTableDescriptionRequest';
import { SetPublishedConnectionTableNameRequest } from '~src/publishedConnectionTable/serverApi/requests/SetPublishedConnectionTableNameRequest';
import { IPublishedConnectionTableActions } from '~src/publishedConnectionTable/serverApi/types/IPublishedConnectionTableActions';

class PublishedConnectionTablesResource implements IListResource<IPublishedConnectionTableInfo>, IDetailResource<IPublishedConnectionTableDetailInfo>, IActionResource<IPublishedConnectionTableActions> {

  private listFetcher: IListFetcher<IPublishedConnectionTableInfo> = new WebClientListResourceFetcher(GetPublishedConnectionTablesRequest, ServerApiMapper.publishedConnectionTables);
  private detailFetcher: IDetailFetcher<IPublishedConnectionTableDetailInfo> = new WebClientDetailFetcher('getPublishedConnectionTable', PublishedConnectionTableMapper.mapDetail);
  private fieldValuesFetcher: IListFetcher<IFieldValue> = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);

  public all(): ResourceQuery<IPublishedConnectionTableInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string): Promise<IPublishedConnectionTableDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'virtualConnectionTable');
  }

  public actions(id: string): Promise<IPublishedConnectionTableActions> {
    return WebHttpClient.sendRequest(new GetPublishedConnectionTableActionsRequest(id));
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetPublishedConnectionTableNameRequest({
      id,
      name
    }));
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToPublishedConnectionTablesRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromPublishedConnectionTablesRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public setDescription = async (args: SetPublishedConnectionTableDescriptionParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetPublishedConnectionTableDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessful(), Messages.editDescriptionFailure()));
  };

  public runExtractRefreshNow(id: string, tableLuids: ReadonlyArray<string>, extractType: string, level: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new RunExtractRefreshesOnPublishedConnectionRequest({
        level,
        startingItems: [...tableLuids],
        targetId: id,
        type: extractType,
      }));
  }

  public setCertification(params: IPublishedConnectionTableCertificationParams & { luid?: string }): Promise<IResult> {
    return updateOrDeleteCertificationForItem({ ...params, contentType: 'virtualConnectionTable' });
  }

  public hasDataQualityWarning(contentItem: IPublishedConnectionTableBase): boolean {
    return contentItem.dataQualityIndicators
      && contentItem.dataQualityIndicators[0]
      && contentItem.dataQualityIndicators[0].isActive;
  }

  public hasCertification(contentItem: IPublishedConnectionTableBase): boolean {
    return contentItem.dataQualityIndicators
      && contentItem.dataQualityIndicators.some(dqi => dqi.isActive && dqi.type === CERTIFIED_INDICATOR_TYPE);
  }

  // Toast
  public toast(result: IResult, successMessage: string, failureMessage?: string): any {
    if (result.errors && result.errors.length > 0) {
      if (failureMessage) {
        this.popError(failureMessage);
      } else {
        result.errors.forEach((error: IError) => {
          this.popError(error.message);
        });
      }
    } else {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: successMessage
      });
    }
  }

  public popError(errorMessage: string): any {
    ToasterService.getInstance().pop({
      type: ToastType.Error,
      text: errorMessage
    });
  }
}

export function isPublishedConnectionTableInfo(item: any): item is IPublishedConnectionTableInfo {
  return item &&
    item.contentType &&
    item.contentType === 'virtualConnectionTable';
}

export const PublishedConnectionTables = new PublishedConnectionTablesResource();
